/**
 * Concept calendar data adapters.
 *
 * Maps the API-shaped medication / appointment / symptom / test-result
 * records into the two shapes the Concept care calendar consumes:
 *
 *   - Per-day marker arrays (`"med" | "visit" | "period" | "lab"`) used to
 *     paint the dots on the grid.
 *   - Per-day `TodayRow[]` used to render the selected-day's agenda.
 *
 * Until this wiring landed the screen was reading from a fixture lookup,
 * so any medication or appointment a user actually added stayed
 * invisible on the calendar (UAT issue #35).
 */

import type {
  ApiAppointment,
  ApiMedication,
} from "@src/packages/care/calendar.types";
import type { TestResult } from "@src/packages/test-results/hooks/useTestResults";
import type { TodayRow } from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import type { MedicationForm } from "@src/packages/homepage/plan/components/MedicationRow";
import { calendarDayOf } from "@src/packages/care/prediction/menstrualBleeding";
import { expandMedicationOccurrenceDates } from "@src/packages/care/medication-occurrences";
import { isMedicationStopped } from "@src/packages/care/medications";
import {
  formatTimeHHMM,
  type Locale,
  type TranslationFunction,
} from "@src/packages/i18n";
import {
  FORM_MAP,
  INTAKE_TO_FORM,
  resolveFormFromContent,
} from "../scheduleHelpers";

export type ConceptDayMarker = "med" | "visit" | "period" | "lab" | "fertile";

// Structural narrow rather than ApiSymptom / LocalSymptom — useSymptoms()
// returns a union of the two and we only need the fields below.
export type ConceptSymptomInput = {
  id: string;
  symptom: string | null;
  dateAndTime: string | null;
};

export type ConceptCalendarInputs = {
  medications: ApiMedication[];
  appointments: ApiAppointment[];
  testResults: TestResult[];
  symptoms: ConceptSymptomInput[];
  startDate?: string;
  endDate?: string;
  /** Translated label shown when a medication has no explicit dose. */
  medicationDoseFallback: string;
  /** Translation function for fallback labels (Any time, Appointment, etc.). */
  t: TranslationFunction;
  /** Locale for time formatting (fr → 24h "9 h 00", en → 12h "9:00 AM"). */
  locale: Locale;
};

/**
 * The calendar day a stored value refers to.
 *
 * Shares `calendarDayOf` rather than splitting on "T" again. Symptom rows
 * arrive as Postgres timestamps — `"2026-06-04 07:35:09.116+00"`, separated by
 * a space — so the old split returned the whole string and the date was
 * rejected: a check-in the patient had just saved drew no marker on its day
 * and no row in that day's list. Appointment and test dates are plain dates and
 * were never affected, which is why only the symptom path looked broken.
 */
function isoDateOnly(value: string | null | undefined): string | null {
  return calendarDayOf(value);
}

function formatTimeLabel(
  time: string | null | undefined,
  locale: Locale,
  t: TranslationFunction,
): string {
  if (!time) return t("home.any_time");
  return formatTimeHHMM(time, locale);
}

// Locale-aware time label for appointment rows. Returns "" when no time is
// set — the row renderer hides the time column entirely in that case.
// Accepts either "HH:MM[:SS]" or an ISO datetime "YYYY-MM-DDTHH:MM:SS..."
// (the appointments API returns the latter on read).
function formatTimeShort(
  time: string | null | undefined,
  locale: Locale,
): string {
  if (!time) return "";
  const isoMatch = /T(\d{2}):(\d{2})/.exec(time);
  if (isoMatch) {
    return formatTimeHHMM(`${isoMatch[1]}:${isoMatch[2]}`, locale);
  }
  return formatTimeHHMM(time, locale);
}

function medicationDoseLabel(med: ApiMedication): string | undefined {
  if (med.dose != null && med.doseUnit) return `${med.dose} ${med.doseUnit}`;
  if (med.dose != null) return String(med.dose);
  return undefined;
}

function medicationName(med: ApiMedication, t: TranslationFunction): string {
  return (
    med.medication?.name ??
    med.rawMedicationName ??
    med.medication?.molecule ??
    t("home.medication_fallback")
  );
}

function medicationForm(
  med: ApiMedication,
  t: TranslationFunction,
): MedicationForm {
  const name = medicationName(med, t);
  const route = med.route?.toLowerCase() ?? "";
  return (
    FORM_MAP[med.form ?? ""] ??
    INTAKE_TO_FORM[route] ??
    resolveFormFromContent(name) ??
    "pill"
  );
}

function medicationTimes(med: ApiMedication): (string | null)[] {
  const value = med.timeTaken as unknown;
  if (Array.isArray(value)) {
    const times = value.filter(
      (time): time is string => typeof time === "string" && time.length > 0,
    );
    return times.length > 0 ? times : ["09:00"];
  }
  if (typeof value === "string" && value.length > 0) return [value];
  return ["09:00"];
}

function scheduledFor(
  iso: string,
  time: string | null | undefined,
): string | null {
  if (!time) return null;
  const match = /(?:T)?(\d{2}):(\d{2})(?::(\d{2}))?/.exec(time);
  if (!match) return null;
  const seconds = match[3] ?? "00";
  return `${iso}T${match[1]}:${match[2]}:${seconds}`;
}

function getMedicationScheduleDates(
  med: ApiMedication,
  inputs: ConceptCalendarInputs,
): string[] {
  // Delegates to the canonical adapter so every surface expands medication
  // occurrences identically (#2223).
  return expandMedicationOccurrenceDates(med, {
    startDate: inputs.startDate,
    endDate: inputs.endDate,
  });
}

export function buildMarkersByDate(
  inputs: ConceptCalendarInputs,
): Map<string, ConceptDayMarker[]> {
  const map = new Map<string, ConceptDayMarker[]>();

  const add = (iso: string, marker: ConceptDayMarker) => {
    const existing = map.get(iso);
    if (existing) {
      if (!existing.includes(marker)) existing.push(marker);
      return;
    }
    map.set(iso, [marker]);
  };

  for (const med of inputs.medications) {
    if (isMedicationStopped(med.notes)) continue;
    for (const iso of getMedicationScheduleDates(med, inputs)) {
      add(iso, "med");
    }
  }
  for (const appt of inputs.appointments) {
    const iso = isoDateOnly(appt.startDate);
    if (iso) add(iso, "visit");
  }
  for (const tr of inputs.testResults) {
    const iso = isoDateOnly(tr.date);
    if (iso) add(iso, "lab");
  }
  // Symptoms share the "lab" marker — the calendar grid has only four
  // marker kinds and a logged check-in is closer to a test entry than to
  // an appointment or medication dose.
  for (const sym of inputs.symptoms) {
    const iso = isoDateOnly(sym.dateAndTime);
    if (iso) add(iso, "lab");
  }

  return map;
}

export function buildTodayRowsByDate(
  inputs: ConceptCalendarInputs,
): Map<string, TodayRow[]> {
  const map = new Map<string, TodayRow[]>();

  const push = (iso: string, row: TodayRow) => {
    const existing = map.get(iso);
    if (existing) {
      existing.push(row);
      return;
    }
    map.set(iso, [row]);
  };

  const { t, locale } = inputs;
  const anyTime = t("home.any_time");

  for (const med of inputs.medications) {
    const scheduleDates = getMedicationScheduleDates(med, inputs);
    if (scheduleDates.length === 0) continue;
    const name = medicationName(med, t);
    const dose = medicationDoseLabel(med);
    const times = medicationTimes(med);
    const stopped = isMedicationStopped(med.notes);
    for (const iso of scheduleDates) {
      for (const time of times) {
        push(iso, {
          id: `med-${med.id}-${iso}${time ? `-${time}` : ""}`,
          time: formatTimeLabel(time, locale, t),
          title: name,
          detail: dose ?? inputs.medicationDoseFallback,
          notes: med.notes ?? undefined,
          icon: "hormones",
          kind: "medication",
          date: iso,
          medicationName: name,
          medicationDose: dose,
          medicationRoute: med.route,
          scheduledFor: scheduledFor(iso, time),
          medicationForm: medicationForm(med, t),
          medicationId: med.id,
          medicationIsStopped: stopped,
          medicationEndDate: med.endDate ?? undefined,
        });
      }
    }
  }

  for (const appt of inputs.appointments) {
    const iso = isoDateOnly(appt.startDate);
    if (!iso) continue;
    const location =
      appt.meetingRoom ??
      (appt.isOnline ? t("home.online") : t("home.clinic_visit"));
    push(iso, {
      id: `appt-${appt.id}`,
      appointmentId: appt.id,
      ownership: appt.ownership,
      ownerId: appt.ownerId,
      time: formatTimeShort(appt.startTime, locale),
      title: appt.title ?? t("home.appointment_fallback"),
      detail: location,
      notes: appt.description ?? undefined,
      icon: "start",
      kind: "appointment",
      date: iso,
      completable: true,
    });
  }

  for (const tr of inputs.testResults) {
    const iso = isoDateOnly(tr.date);
    if (!iso) continue;
    const value = tr.doctorValue ?? tr.patientValue ?? tr.value;
    const detail = value
      ? `${value}${tr.unit ? ` ${tr.unit}` : ""}`
      : t("home.result_recorded");
    push(iso, {
      id: `test-${tr.id}`,
      time: anyTime,
      title: tr.name ?? t("home.test_result_fallback"),
      detail,
      icon: "panel",
      kind: "monitoring",
      date: iso,
    });
  }

  for (const sym of inputs.symptoms) {
    const iso = isoDateOnly(sym.dateAndTime);
    if (!iso) continue;
    push(iso, {
      id: `symptom-${sym.id}`,
      time: anyTime,
      title: t("home.daily_check_in"),
      detail: sym.symptom
        ? t("home.logged_with_symptom", { params: { symptom: sym.symptom } })
        : t("home.symptoms_logged"),
      icon: "process",
      kind: "task",
      date: iso,
    });
  }

  // Stable sort per day so the agenda is predictable: morning meds before
  // afternoon appointments, lab values last.
  for (const [iso, rows] of map.entries()) {
    map.set(
      iso,
      [...rows].sort((a, b) => a.time.localeCompare(b.time)),
    );
  }

  return map;
}
